import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { signWaiver } from '@/domain/waiverFlow';
import WaiverAgreementForm from '@/components/WaiverAgreementForm';
import { format } from 'date-fns';
import { CheckCircle2 } from 'lucide-react';
import type { WaiverAnswers } from '@/domain/types';

/**
 * Customer-facing waiver signing page. Reached by a public link keyed on the
 * waiver id (`/customer-waiver/:waiverId`) — no login, mirroring the deposit
 * self-book page. Renders the full Addikted to Ink agreement and captures the
 * medical declaration, consents, identity and a hold-to-sign signature.
 */
export default function WaiverSignPage() {
  const { waiverId } = useParams<{ waiverId: string }>();
  const initialised = useBrainStore((s) => s.initialised);
  const waiver = useBrainStore((s) => (waiverId ? s.waivers[waiverId] : undefined));
  const customer = useBrainStore((s) => (waiver ? s.customers[waiver.customerId] : undefined));
  const booking = useBrainStore((s) => (waiver ? s.bookings[waiver.bookingId] : undefined));
  const artist = useBrainStore((s) => (booking ? s.artists[booking.artistId] : undefined));

  const [justSigned, setJustSigned] = useState(false);

  // Studio data loads asynchronously (large seed). Until it's ready, show a
  // loading state — a missing waiver here just means "not loaded yet", not an
  // invalid link.
  if (!initialised) {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center animate-pulse" data-testid="waiver-loading">
          Loading your waiver…
        </p>
      </CustomerShell>
    );
  }

  if (!waiver || !customer) {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center">
          This waiver link is invalid or has expired. Please contact the studio.
        </p>
      </CustomerShell>
    );
  }

  const alreadySigned = waiver.status === 'signed';

  if (justSigned || alreadySigned) {
    return (
      <CustomerShell>
        <div className="text-center py-8" data-testid="waiver-sign-success">
          <CheckCircle2 size={48} className="text-mint mx-auto" />
          <h1 className="heading-display text-2xl text-cream mt-4">All signed</h1>
          <p className="font-serif text-cream-soft mt-2">
            Thanks {customer.firstName} — your medical waiver is on file
            {booking ? ` for your session on ${format(new Date(booking.start), 'EEEE d MMMM')}` : ''}.
            {artist ? ` See you soon, ${artist.displayName} is looking forward to it.` : ''}
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-6">
            Nothing else to do — see you at the studio.
          </p>
        </div>
      </CustomerShell>
    );
  }

  const onSigned = (answers: WaiverAnswers) => {
    signWaiver({ waiverId: waiver.id, answers });
    setJustSigned(true);
  };

  return (
    <CustomerShell>
      <header className="text-center mb-5">
        <p className="label-small-caps text-gold-bright">Pre-session medical waiver</p>
        <h1 className="heading-display text-2xl text-cream mt-1">Hi {customer.firstName}</h1>
        <p className="font-serif text-cream-soft text-sm mt-2">
          Please review and sign before your{' '}
          {booking ? booking.sessionLength.replace('-', ' ') : ''} session
          {artist ? ` with ${artist.displayName}` : ''}
          {booking ? ` on ${format(new Date(booking.start), 'EEEE d MMMM')}` : ''}.
        </p>
      </header>

      <WaiverAgreementForm customer={customer} booking={booking} variant="customer" onSigned={onSigned} />
    </CustomerShell>
  );
}

function CustomerShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen w-full bg-ink-black text-cream flex flex-col items-center px-6 py-8">
      <div className="max-w-md w-full">
        <p className="font-cinzel-deco text-xs text-gold-bright tracking-widest text-center mb-6">
          ADDIKTED TO INK
        </p>
        {children}
        <p className="font-inter text-[9px] text-cream-soft/70 text-center mt-10 uppercase tracking-widest">
          Secure signing link. Mock prototype — no data leaves the studio.
        </p>
      </div>
    </div>
  );
}
